import CMSWrapper from '@/features/shared/CMSWrapper';
import ContentContainer from '@/features/shared/ContentContainer';
import { withGTM } from '@/features/shared/HOCs';
import { MuiContainer } from '@/features/shared/material-ui';
import { ColorNames, SpacingSizes } from '@/utils/constants';
import { resolveImageUrl, resolveRTC } from '@/utils/resolvers';
import { getOr } from 'lodash/fp';
import React, { memo, useRef } from 'react';
import PriceList from './PriceList';
import { DOWNLOAD_EVENT_NAME, onDatalayerDownloadEvent } from './PriceList.datalayer';

const PriceListMapper = memo(
  ({
    content,
    contentButtons,
    config = {},
    labels = {},
    priceListTypes = {},
    presentation = {},
  }: any) => {
    const valueList = Object.keys(priceListTypes).map((key) => ({
      key,
      value: priceListTypes[key],
    }));

    const priceListValueListMapper = (fileTypes: any[]) =>
      fileTypes.map((fileType) => ({
        iconUrl: resolveImageUrl({ raw: getOr('', `${fileType.key}_icon`, config) }),
        label: getOr('', `${fileType.key}_label`, labels),
        downloadProps: {
          url: 'portal/pricelist',
          fileType: fileType.key,
        },
        downloadIconUrl: resolveImageUrl({ raw: config?.download_icon }),
      }));

    const priceListProps = {
      title: content?.title,
      description: content?.description,
      items: priceListValueListMapper(valueList),
      priceListLabels: labels,
      downloadProvider: config?.download_provider,
    };

    return (
      <div
        data-datalayer-position="price_list"
        data-datalayer-variant={presentation?.datalayerVariant}
      >
        <ContentContainer
          topSpacing={SpacingSizes.small}
          bottomSpacing={SpacingSizes.small}
          topBackgroundColor={ColorNames.charcoal}
          bottomBackgroundColor={ColorNames.charcoal}
        >
          <MuiContainer className="medium">
            <PriceList {...priceListProps} contentButtons={contentButtons} />
          </MuiContainer>
        </ContentContainer>
      </div>
    );
  },
);

const PriceListMapperWithGTM = withGTM({
  Component: PriceListMapper,
  onDatalayer: onDatalayerDownloadEvent,
  config: { eventName: DOWNLOAD_EVENT_NAME, isCustom: true },
});

const componentResolver = ({ rawVal = '' }: any) => rawVal;

const PriceListCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    document: [
      'content',
      {
        heading: ['title', componentResolver],
        detailContent: ['description', resolveRTC],
      },
    ],
    'priceList.config': 'config',
    'priceList.labels': 'labels',
    'price-list-types': 'priceListTypes',
    componentParameterMap: [
      'presentation',
      {
        'variant-label': 'datalayerVariant',
      },
    ],
  });

  const requiredFields = useRef({
    document: '<b>Price List Content</b> is required',
  });

  const contentButtonNames = useRef({
    document: 'content',
  });

  return (
    <>
      <CMSWrapper
        brProps={brProps}
        render={PriceListMapperWithGTM}
        mapping={mapping.current}
        requiredFields={requiredFields.current}
        contentButtonNames={contentButtonNames.current}
      />
    </>
  );
};

export default PriceListCMSWrapper;
